import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ClassificationService } from '../../services/classification/classification.service';
import { catchError, map, of } from 'rxjs';
import { IClassification } from '../../models/classification.model';

export interface ReferralOption {
  id: string;
  label: string;
  selected: boolean;
}

export interface Classification {
  code: string;
  display: string;
  selected: boolean;
}

export interface ReferralDialogData {
  patientHistory: number | string;
  transcription: string
}

export interface ReferralDialogResult {
  selectedClassifications: string[];
  includepatientHistory: boolean;
}

@Component({
    standalone: false,
    selector: 'cva-referral-dialog',
    templateUrl: './referral-dialog.component.html',
    styleUrl: './referral-dialog.component.scss',
    
})
export class ReferralDialogComponent implements OnInit {
  classifications: Classification[] = [];
  includepatientHistory: boolean = false;
  isLoading = true;

  constructor(
    public dialogRef: MatDialogRef<ReferralDialogComponent>,
    private readonly classificationService: ClassificationService,
    @Inject(MAT_DIALOG_DATA) public data: ReferralDialogData
  ) { }

  get isPatientHistoryDisabled(): boolean {
    return this.data.patientHistory === 0 || this.data.patientHistory === '0';
  }

  get patientHistoryTooltip(): string {
    if (this.isPatientHistoryDisabled) {
      return 'To enable this option, update Preferences to include patient history.';
    }
    return 'Use patient history as additional context for a more accurate referral generation.';
  }

  ngOnInit(): void {
    // Get classifications from localStorage
    if (this.isPatientHistoryDisabled) {
      this.includepatientHistory = false;
    }

    this.loadClassificationsFromStorage();
  }

  loadClassificationsFromStorage(): void {
    try {
      const storedClassifications = localStorage.getItem('recommendedClassifications');
      let parsedClassifications = [];
      if(storedClassifications) {
        parsedClassifications = JSON.parse(storedClassifications);
      }
      if (parsedClassifications?.length > 0) {
        // Map stored classifications to our format
        this.isLoading = false;
        this.classifications = parsedClassifications.map((item: { code: string; display: string }) => ({
          code: item.code,
          display: item.display,
          selected: false
        }));
      } else {
        this.classificationService.generateClassifications(this.data.transcription)
          .pipe(
            catchError((err) => {
              this.isLoading = false;
              console.error('Failed to generate recommended classifications', err);
              return of({ response: [] as IClassification[] });
            }),
            map((result: { response: IClassification[] }) => {
              const classifications = Array.isArray(result?.response) ? result?.response : [];
              this.isLoading = false
              this.classifications = classifications.map((item: { code: string; display: string }) => ({
                code: item.code,
                display: item.display,
                selected: false
              }));
              this.classificationService.setClassifications(classifications, false);
              return classifications.length > 0;
            })
          )
          .subscribe((hasData) => {
            console.log('Has classifications:', hasData);
          });
      }
    } catch (error) {
      console.error('Error loading classifications from localStorage:', error);
      this.classifications = [];
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onProceed(): void {
    const selectedClassifications = this.classifications
      .filter(classification => classification.selected)
      .map(classification => classification.display);

    const result: ReferralDialogResult = {
      selectedClassifications,
      includepatientHistory: this.includepatientHistory
    };

    this.dialogRef.close(result);
  }

  toggleOption(option: ReferralOption): void {
    option.selected = !option.selected;
  }

  toggleClassification(classification: Classification): void {
    classification.selected = !classification.selected;
  }
}
